import {announce} from '@github-ui/aria-live'
import {
  type CreateScribeArtifactInput,
  type ScribeArtifact,
  useCreateScribeArtifact,
  useUpdateScribeArtifact,
} from '@github-ui/scribe-artifacts'
import {isScribeArtifactsValidationError} from '@github-ui/scribe-artifacts/services'
import {useCallback, useState} from 'react'

type CreateArtifactSubmitInput = Pick<CreateScribeArtifactInput, 'name' | 'description' | 'body' | 'type'>

type UseCreateArtifactSubmitArgs = {
  resourceId: number
  artifactNumber?: number
  onClose: () => void
  onCreated?: () => void
  onUpdated?: (artifact: ScribeArtifact) => void
  errorMessage: string
}

export function useCreateArtifactSubmit({
  resourceId,
  artifactNumber,
  onClose,
  onCreated,
  onUpdated,
  errorMessage,
}: UseCreateArtifactSubmitArgs) {
  const [createErrorMessage, setCreateErrorMessage] = useState('')
  const {mutate: createArtifact, isPending: createIsPending} = useCreateScribeArtifact({
    resourceType: 'issue',
    resourceId,
  })
  const {mutate: updateArtifact, isPending: updateIsPending} = useUpdateScribeArtifact({
    resourceType: 'issue',
    resourceId,
  })
  const isPending = createIsPending || updateIsPending

  const clearCreateErrorMessage = useCallback(() => {
    setCreateErrorMessage('')
  }, [])

  const submitArtifact = useCallback(
    (input: CreateArtifactSubmitInput) => {
      const options = {
        onSuccess: (savedArtifact: ScribeArtifact) => {
          if (artifactNumber !== undefined) {
            onUpdated?.(savedArtifact)
            announce('Artifact updated successfully.')
          }
          onCreated?.()
          onClose()
        },
        onError: (error: Error) => {
          const displayedErrorMessage = isScribeArtifactsValidationError(error) ? error.message : errorMessage
          setCreateErrorMessage(displayedErrorMessage)
          announce(displayedErrorMessage, {assertive: true})
        },
      }

      if (artifactNumber !== undefined) {
        updateArtifact({number: artifactNumber, name: input.name, body: input.body}, options)
      } else {
        createArtifact(input, options)
      }
    },
    [artifactNumber, createArtifact, errorMessage, onClose, onCreated, onUpdated, updateArtifact],
  )

  return {clearCreateErrorMessage, createErrorMessage, isPending, submitArtifact}
}
